import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { AlertCircle, Check, Plus, Save, Trash2 } from 'lucide-react'
import {
  getTemplateContent,
  getUserTemplates,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from '../actions/templateActions'
import {
  CREATE_TEMPLATE_RESET,
  GET_TEMPLATE_CONTENT_RESET,
  UPDATE_TEMPLATE_RESET,
  DELETE_TEMPLATE_RESET,
} from '../constants/templateConstants'
import SEO from '../components/SEO'
import './Template.css'

// Defined outside the component so it is a stable reference and never has to
// be listed as an effect dependency.
const syncDialog = (ref, open) => {
  const node = ref.current
  if (!node) return
  if (open && !node.open) node.showModal()
  if (!open && node.open) node.close()
}

function Template() {
  const dispatch = useDispatch()

  const [showNewDialog, setShowNewDialog] = useState(false)
  const [showLoginDialog, setShowLoginDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [newTemplateName, setNewTemplateName] = useState('')
  const [currentTemplate, setCurrentTemplate] = useState(null)
  const [currentTemplateName, setCurrentTemplateName] = useState('')
  const [content, setContent] = useState('')
  const [status, setStatus] = useState('')

  const newDialogRef = useRef(null)
  const loginDialogRef = useRef(null)
  const deleteDialogRef = useRef(null)

  const { userInfo } = useSelector((state) => state.userLogin)
  const { userTemplatesLoading, userTemplates } = useSelector((state) => state.userTemplateReducer)
  const { templateContent } = useSelector((state) => state.templateContentReducer)
  const { templateCreated } = useSelector((state) => state.templateCreateReducer)
  const { templateUpdating, templateUpdated } = useSelector((state) => state.templateUpdateReducer)
  const { templateDeleting, templateDeleted, templateDeletedError } = useSelector(
    (state) => state.templateDeleteReducer
  )

  const templates = userTemplates && userTemplates.templates ? userTemplates.templates : []

  // Refetch whenever the store has no template list — create/delete clear it,
  // which is how the list refreshes after a change.
  useEffect(() => {
    if (!userTemplates) {
      dispatch(getUserTemplates(userInfo ? userInfo.id : -1))
    }
  }, [userTemplates, userInfo, dispatch])

  // Re-attach the selected template once a fresh list arrives. Depends on
  // userTemplates, not the derived array — that literal is a new [] on every
  // render while the store is empty, which would re-run this constantly.
  useEffect(() => {
    const list = userTemplates && userTemplates.templates ? userTemplates.templates : []
    if (!list.length || currentTemplateName === '') return
    const entry = list.find((e) => e.name === currentTemplateName)
    if (entry && (currentTemplate === null || entry.id !== currentTemplate.id)) {
      setCurrentTemplate(entry)
    }
  }, [userTemplates, currentTemplateName, currentTemplate])

  useEffect(() => {
    if (templateContent == null) return
    setContent(templateContent.content)
    dispatch({ type: GET_TEMPLATE_CONTENT_RESET })
  }, [templateContent, dispatch])

  useEffect(() => {
    if (templateCreated) {
      setStatus('Template created')
      dispatch({ type: CREATE_TEMPLATE_RESET })
    }
    if (templateUpdated) {
      setStatus('Template saved')
      dispatch({ type: UPDATE_TEMPLATE_RESET })
    }
    if (templateDeleted) {
      setStatus('Template deleted')
      dispatch({ type: DELETE_TEMPLATE_RESET })
    }
  }, [templateCreated, templateUpdated, templateDeleted, dispatch])

  // Native <dialog> gives focus trapping, Esc-to-close and ::backdrop for free.
  useEffect(() => syncDialog(newDialogRef, showNewDialog), [showNewDialog])
  useEffect(() => syncDialog(loginDialogRef, showLoginDialog), [showLoginDialog])
  useEffect(() => syncDialog(deleteDialogRef, showDeleteDialog), [showDeleteDialog])

  const handleSelectChange = (event) => {
    const entry = templates.find((e) => String(e.id) === event.target.value)
    if (!entry) {
      setCurrentTemplate(null)
      setCurrentTemplateName('')
      setContent('')
      return
    }
    setStatus('')
    setCurrentTemplate(entry)
    setCurrentTemplateName(entry.name)
    dispatch(getTemplateContent(entry.id))
  }

  const handleAddTemplate = () => {
    if (!userInfo) {
      setShowLoginDialog(true)
      return
    }
    setNewTemplateName('')
    setShowNewDialog(true)
  }

  const handleCreateNewTemplate = () => {
    setShowNewDialog(false)
    setCurrentTemplate(null)
    setCurrentTemplateName(newTemplateName)
    setContent('')
    dispatch(createTemplate(newTemplateName, userInfo.id, 1))
  }

  const handleTemplateUpdate = () => {
    if (currentTemplate == null) {
      setShowNewDialog(true)
      return
    }
    dispatch(
      updateTemplate(currentTemplate.id, {
        userId: userInfo.id,
        name: currentTemplate.name,
        content,
      })
    )
  }

  const handleDeleteTemplate = () => {
    if (!currentTemplate) return
    const id = currentTemplate.id
    setShowDeleteDialog(false)
    setCurrentTemplate(null)
    setCurrentTemplateName('')
    setContent('')
    dispatch(deleteTemplate(id, userInfo.id))
  }

  const busy = userTemplatesLoading || templateUpdating || templateDeleting

  return (
    <>
      <SEO
        title="Templates - PCRAssist"
        description="Create and edit the report templates PCRAssist merges into your narrative."
        canonicalUrl="/template"
      />

      <div className="tp">
        <div className="tp__wrap">
          <header className="tp__head">
            <h1 className="tp__title">Templates</h1>
            <p className="tp__lede">
              Standard statements for a call type. Where a template and your narrative conflict,
              what you wrote supersedes the template.
            </p>
          </header>

          {templateDeletedError && (
            <p className="tp__error" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>{templateDeletedError}</span>
            </p>
          )}

          <div className="tp__pick">
            <div className="tp__field">
              <label className="tp__label" htmlFor="tp-select">
                Template
              </label>
              <select
                id="tp-select"
                className="tp__select"
                value={currentTemplate ? String(currentTemplate.id) : ''}
                onChange={handleSelectChange}
                disabled={busy}
              >
                <option value="">
                  {templates.length ? 'Select a template…' : 'No templates yet'}
                </option>
                {templates.map((entry) => (
                  <option key={entry.id} value={String(entry.id)}>
                    {entry.name}
                  </option>
                ))}
              </select>
            </div>

            <button type="button" className="tp__btn" onClick={handleAddTemplate} disabled={busy}>
              <Plus size={16} aria-hidden="true" />
              New template
            </button>
          </div>

          {currentTemplate ? (
            <>
              <div className="tp__surface">
                <div className="tp__surface-bar">
                  <label className="tp__bar-label" htmlFor="tp-editor">
                    Template text
                  </label>
                  <span className="tp__name">{currentTemplate.name}</span>
                </div>
                <textarea
                  id="tp-editor"
                  className="tp__editor"
                  value={content}
                  onChange={(e) => {
                    setContent(e.target.value)
                    setStatus('')
                  }}
                  disabled={!userInfo || busy}
                />
              </div>

              <div className="tp__actions">
                <button
                  type="button"
                  className="tp__btn tp__btn--primary"
                  onClick={handleTemplateUpdate}
                  disabled={!userInfo || busy}
                >
                  <Save size={16} aria-hidden="true" />
                  {templateUpdating ? 'Saving…' : 'Save'}
                </button>

                {status && (
                  <span className="tp__status" role="status">
                    <Check size={16} aria-hidden="true" />
                    {status}
                  </span>
                )}

                <button
                  type="button"
                  className="tp__btn tp__btn--danger tp__actions-spacer"
                  onClick={() => setShowDeleteDialog(true)}
                  disabled={!userInfo || busy}
                >
                  <Trash2 size={16} aria-hidden="true" />
                  Delete
                </button>
              </div>
            </>
          ) : (
            <p className="tp__empty">
              {templates.length
                ? 'Pick a template above to edit it, or create a new one.'
                : 'You don’t have any templates yet. Create one to get started.'}
            </p>
          )}
        </div>

        {/* ---- new template ---------------------------------------- */}
        <dialog
          ref={newDialogRef}
          className="tp__dialog"
          onClose={() => setShowNewDialog(false)}
          aria-labelledby="tp-new-title"
        >
          <div className="tp__dialog-head">
            <h2 id="tp-new-title">New template</h2>
          </div>
          <div className="tp__dialog-body">
            <div className="tp__field">
              <label className="tp__label" htmlFor="tp-new-name">
                Name
              </label>
              <input
                id="tp-new-name"
                className="tp__input"
                type="text"
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                placeholder="Chest pain"
              />
            </div>
          </div>
          <div className="tp__dialog-foot">
            <button
              type="button"
              className="tp__btn tp__dialog-foot-spacer"
              onClick={() => setShowNewDialog(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="tp__btn tp__btn--primary"
              onClick={handleCreateNewTemplate}
              disabled={!newTemplateName.trim()}
            >
              Create
            </button>
          </div>
        </dialog>

        {/* ---- login required -------------------------------------- */}
        <dialog
          ref={loginDialogRef}
          className="tp__dialog"
          onClose={() => setShowLoginDialog(false)}
          aria-labelledby="tp-login-title"
        >
          <div className="tp__dialog-head">
            <h2 id="tp-login-title">Sign in required</h2>
          </div>
          <div className="tp__dialog-body">
            <p>Only signed-in users can create their own templates.</p>
          </div>
          <div className="tp__dialog-foot">
            <button
              type="button"
              className="tp__btn tp__dialog-foot-spacer"
              onClick={() => setShowLoginDialog(false)}
            >
              Close
            </button>
            <Link className="tp__btn tp__btn--primary" to="/login">
              Sign in
            </Link>
          </div>
        </dialog>

        {/* ---- delete confirmation --------------------------------- */}
        <dialog
          ref={deleteDialogRef}
          className="tp__dialog"
          onClose={() => setShowDeleteDialog(false)}
          aria-labelledby="tp-delete-title"
        >
          <div className="tp__dialog-head">
            <h2 id="tp-delete-title">Delete this template?</h2>
          </div>
          <div className="tp__dialog-body">
            <p>
              <strong>{currentTemplate ? currentTemplate.name : ''}</strong> and its text will be
              removed. This cannot be undone.
            </p>
          </div>
          <div className="tp__dialog-foot">
            <button
              type="button"
              className="tp__btn tp__dialog-foot-spacer"
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="tp__btn tp__btn--danger"
              onClick={handleDeleteTemplate}
              disabled={templateDeleting}
            >
              <Trash2 size={16} aria-hidden="true" />
              {templateDeleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </dialog>
      </div>
    </>
  )
}

export default Template
